import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { TouchableOpacity, View, StyleSheet, Keyboard, } from 'react-native';
import Alert from 'utils/packages/Alert';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { AppView, Button, CardView, CollapsableSettingsSection, rem, SettingsItemDescription, SettingsItemInput, SettingsItemMoneyInput, SettingsItemNavigate, Spacer, StatusBar, withAnimatedNavigationHeader, } from 'design-system';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectActiveConnections, selectDefaultCurrency, } from 'reducers/selectors';
import { IconSmartRule, IconBoldArrow, IconBoldCalendar, IconBank, } from 'design-system/icons';
import IconTooltip from 'design-system/icons/IconTooltip';
import useColors from 'hooks/useColors';
import Text from 'design-system/Text';
import { smartRuleTypeLabels } from 'utils/types/navigation';
import Modal from 'utils/packages/Modal';
import SelectDateRangeModalContent from 'features/subscriptions/components/modal/SelectDateRangeModalContent';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { editFilter, editSmartRule, getFilterPreview, } from 'actions';
import getListOfflineAccounts from 'utils/api/getListOfflineAccounts';
import MultiSelectAccountModal from 'design-system/MultiSelectAccountModal';
import { IconLetters } from 'design-system/icons/IconLetters';
import { muteErrorAndFetching, useFetchOld, useKeyboardAdjustPan } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { PHYSICAL_ASSET_TYPES } from 'features/accounts/constants';
import { SmartRuleIconForTransactionType } from '../components/SmartRuleIconForTransactionType';
import { SmartRuleDetectedTransactions } from '../components/SmartRuleDetectedTransactions';
import { SmartRuleTransactionTypeModal } from '../components/SmartRuleTransactionTypeModal';
import { getPeriodText } from '../utils';
import { EDIT_SMART_RULE_SUCCESS } from '../actions';
import { isAndroid } from '../../../constants';
const strings = {
    title: 'Edit a rule',
};
const extraHeight = isAndroid ? rem(16) : rem(116);
const SmartRuleEditScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    useKeyboardAdjustPan();
    const currency = useAppSelector(selectDefaultCurrency);
    const currencySymbol = useMemo(() => getSymbolFromCurrency(currency), [currency]);
    const [isFetching, setIsFetching] = useState(false);
    const nameRef = useRef(null);
    const referenceRef = useRef(null);
    const minAmountRef = useRef(null);
    const maxAmountRef = useRef(null);
    const { criteria } = route.params.filter;
    const [filters, setFilters] = useState({
        accountIds: criteria.accountIds,
        counterpartRegex: criteria.counterpartRegex,
        minAmount: criteria.minAmount,
        maxAmount: criteria.maxAmount,
        minDayOfMonth: criteria.minDayOfMonth ?? 1,
        maxDayOfMonth: criteria.maxDayOfMonth ?? 31,
        type: criteria.type,
    });
    const [rule, setRule] = useState({
        ruleName: route.params.ruleName || '',
        targetAccountId: route.params.targetAccountId,
        isActive: route.params.isActive,
    });
    const [forceOpen, forceOpenFilters] = useState({});
    const colors = useColors();
    const dispatch = useAppDispatch();
    const activeConnections = useAppSelector(selectActiveConnections);
    const handleChangeFilters = useCallback((key, value) => {
        setFilters((oldFilters) => ({ ...oldFilters, [key]: value }));
    }, []);
    const [initialTransactions, , , fetchInitialTxns] = useFetchOld(muteErrorAndFetching);
    useEffect(() => {
        fetchInitialTxns(getFilterPreview({ accountIds: `[${filters.accountIds.join(',')}]` }, 1));
    }, [fetchInitialTxns, filters.accountIds]);
    const handlePickTransaction = useCallback(() => {
        navigation.navigateDeprecated('SmartRuleSelectTransaction', {
            initialTransactions: initialTransactions?.transactions,
            selectedAccounts: filters.accountIds,
            onSelect: (v) => handleChangeFilters('counterpartRegex', v),
        });
    }, [filters.accountIds, handleChangeFilters, initialTransactions]);
    const handleSave = useCallback(async () => {
        Keyboard.dismiss();
        const { counterpartRegex, minAmount, maxAmount, minDayOfMonth, maxDayOfMonth, type, accountIds, } = filters;
        if (!rule.ruleName) {
            Alert.alert('Hold on 💫', 'You need to add a name.', [
                { text: 'OK', onPress: () => nameRef.current?.focus() },
            ]);
            return;
        }
        if (!counterpartRegex) {
            Alert.alert('Hold on 💫', 'You need to set a reference.', [
                { text: 'OK', onPress: () => referenceRef.current?.focus() },
            ]);
            return;
        }
        if (minAmount && maxAmount && minAmount > maxAmount) {
            Alert.alert('Hold on 💫', 'Minimum amount should be less than maximum.', [
                {
                    text: 'OK',
                    onPress: () => {
                        forceOpenFilters({});
                        minAmountRef.current?.focus();
                    },
                },
            ]);
            return;
        }
        setIsFetching(true);
        const body = { criteria: {} };
        body.criteria.accountIds = accountIds;
        body.criteria.counterpartRegex = counterpartRegex;
        body.criteria.type = type;
        body.criteria.minDayOfMonth = minDayOfMonth;
        body.criteria.maxDayOfMonth = maxDayOfMonth;
        if (minAmount)
            body.criteria.minAmount = minAmount;
        if (maxAmount)
            body.criteria.maxAmount = maxAmount;
        await dispatch(editFilter(route.params.filterId, body));
        const response = await dispatch(editSmartRule(route.params.id, rule));
        if (response?.type === EDIT_SMART_RULE_SUCCESS) {
            navigation.navigateDeprecated('SmartRuleDetails', {
                id: route.params.id,
            });
        }
        setIsFetching(false);
    }, [filters, route.params.filterId, route.params.id, rule]);
    const offlineAccounts = useMemo(() => getListOfflineAccounts(activeConnections), [activeConnections]);
    const accounts = useMemo(() => getListOfflineAccounts(activeConnections, true), [activeConnections]);
    const scrollValue = useSharedValue(0);
    const onScroll = useAnimatedScrollHandler((handle) => {
        scrollValue.value = handle.contentOffset.y;
    }, []);
    const handlePickFromAccounts = useCallback(() => {
        Keyboard.dismiss();
        MultiSelectAccountModal.show({
            onScroll,
            scrollValue,
            accounts,
            onSelected: (ids) => handleChangeFilters('accountIds', ids),
            alreadySelected: filters.accountIds,
        });
    }, [
        accounts,
        filters.accountIds,
        handleChangeFilters,
        onScroll,
        scrollValue,
    ]);
    const handlePickType = useCallback(() => {
        Keyboard.dismiss();
        SmartRuleTransactionTypeModal.show({
            onSelect: (type) => handleChangeFilters('type', type),
            selected: filters.type,
        });
    }, [filters.type, handleChangeFilters]);
    const handlePickPeriod = useCallback(() => {
        Keyboard.dismiss();
        Modal.show(<SelectDateRangeModalContent data={{
                start: filters.minDayOfMonth || undefined,
                end: filters.maxDayOfMonth || undefined,
            }} onFinish={(range) => {
                setFilters((oldFilters) => ({
                    ...oldFilters,
                    minDayOfMonth: range.start,
                    maxDayOfMonth: range.end,
                }));
                Modal.hide();
            }}/>);
    }, [filters.maxDayOfMonth, filters.minDayOfMonth]);
    const period = useMemo(() => getPeriodText(filters.minDayOfMonth, filters.maxDayOfMonth), [filters.maxDayOfMonth, filters.minDayOfMonth]);
    const fromText = useMemo(() => {
        if (filters.accountIds.length === 1) {
            const foundAccount = accounts.find((acc) => filters.accountIds[0] === acc.id);
            if (foundAccount) {
                return foundAccount.name;
            }
        }
        return `${filters.accountIds.length} accounts selected`;
    }, [accounts, filters.accountIds]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const targetAccount = useMemo(() => offlineAccounts.find((acc) => acc.id === rule.targetAccountId), [offlineAccounts, rule.targetAccountId]);
    const modalOfflineAccounts = useMemo(() => {
        const filteredAccounts = offlineAccounts.filter((account) => !PHYSICAL_ASSET_TYPES.has(account.type) ||
            account.id === route.params.targetAccountId);
        return filteredAccounts;
    }, [offlineAccounts, route.params.targetAccountId]);
    const handlePickOfflineAccount = useCallback(() => {
        Keyboard.dismiss();
        MultiSelectAccountModal.show({
            onScroll,
            scrollValue,
            accounts: modalOfflineAccounts,
            onSelected: (ids) => setRule((prev) => ({ ...prev, targetAccountId: ids[0] })),
            alreadySelected: [rule.targetAccountId],
            isMultipleSelect: false,
        });
    }, [modalOfflineAccounts, onScroll, rule.targetAccountId, scrollValue]);
    if (!targetAccount)
        return null;
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <AnimatedKeyboardAwareScrollView onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator={false} contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="handled" enableResetScrollToCoords={false} enableOnAndroid extraHeight={extraHeight}>
          <CardView>
            <SettingsItemInput ref={nameRef} description="Name" icon={<IconSmartRule />} value={rule.ruleName} onChangeText={(v) => setRule((prev) => ({ ...prev, ruleName: v }))} placeholder="Enter name" iconSize={44} returnKeyType="done"/>
          </CardView>
          <CollapsableSettingsSection title="Detect transactions from" collapsed={false} enabled={false} style={styles.collapsible}>
            <SettingsItemNavigate icon={<IconBank />} title={fromText} description="From" onPress={handlePickFromAccounts}/>
            <SettingsItemInput ref={referenceRef} description={<View style={styles.referenceContainer}>
                  <SettingsItemDescription>Reference</SettingsItemDescription>
                  <TouchableOpacity onPress={() => navigation.navigateDeprecated('SmartRuleReferenceInfo')}>
                    <IconTooltip width={16} height={16} color={colors.text.secondary}/>
                  </TouchableOpacity>
                </View>} subDescription={<>
                  <Spacer h={4}/>
                  <TouchableOpacity onPress={handlePickTransaction} style={styles.referenceContainer}>
                    <IconSmartRule gradientKey="ultimate" width={16} height={16}/>
                    <Text TextThin-14 Primary>
                      Pick from transactions
                    </Text>
                  </TouchableOpacity>
                </>} icon={<IconLetters gradientKey="darkPurple"/>} value={filters.counterpartRegex} onChangeText={(v) => handleChangeFilters('counterpartRegex', v)} placeholder="Type..." iconSize={44} returnKeyType="done"/>
          </CollapsableSettingsSection>
          <CollapsableSettingsSection title="Advanced Filters" collapsed forceOpen={forceOpen} style={styles.collapsible}>
            <SettingsItemMoneyInput ref={minAmountRef} icon={<IconBoldArrow direction="left"/>} value={filters.minAmount ? filters.minAmount.toString() : ''} onClearTextInput={() => handleChangeFilters('minAmount', 0)} underlineColorAndroid="transparent" onChangeText={(_, v) => handleChangeFilters('minAmount', v === undefined ? undefined : Number(v))} description="Minimum amount (optional)" placeholder="Set value..." returnKeyType="done" prefix={currencySymbol} maximumFractionalDigits={0} placeholderTextColor={colors.text.placeholder}/>
            <SettingsItemMoneyInput ref={maxAmountRef} icon={<IconBoldArrow direction="right" gradientKey="skyBlue"/>} onClearTextInput={() => handleChangeFilters('maxAmount', 0)} value={filters.maxAmount ? filters.maxAmount.toString() : ''} underlineColorAndroid="transparent" onChangeText={(_, v) => handleChangeFilters('maxAmount', v === undefined ? undefined : Number(v))} description="Maximum amount (optional)" placeholder="Set value..." returnKeyType="done" prefix={currencySymbol} maximumFractionalDigits={0} placeholderTextColor={colors.text.placeholder}/>
            <SettingsItemNavigate title={smartRuleTypeLabels[filters.type]} icon={<SmartRuleIconForTransactionType type={filters.type}/>} onPress={handlePickType} description="Transaction type"/>
            <SettingsItemNavigate title={<Text Text-16>{period}</Text>} icon={<IconBoldCalendar />} description="Period" onPress={handlePickPeriod}/>
          </CollapsableSettingsSection>
          <SmartRuleDetectedTransactions filters={filters}/>
          <CollapsableSettingsSection title="Create transactions to" collapsed={false} enabled={false} style={styles.collapsible}>
            <SettingsItemNavigate icon={<IconBank />} title={targetAccount.name} description="To" onPress={handlePickOfflineAccount}/>
          </CollapsableSettingsSection>
        </AnimatedKeyboardAwareScrollView>
        <Button floating brand title="Save" onPress={handleSave} isFetching={isFetching}/>
      </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    referenceContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: rem(8),
    },
    infoCard: {
        marginTop: rem(16),
    },
    collapsible: {
        marginTop: 0,
        marginBottom: rem(16),
    },
});
export default SmartRuleEditScreen;
